import React from 'react';
import { Scale, Briefcase, Package, Plus, Minus } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент боковой панели быстрой настройки багажа
 */
const QuickAdjustmentSidebar = ({ 
  formData,
  setFormData,
  setErrors,
  errors,
  isCalculating,
  airlinesData,
  travelClasses,
  destinationTypes
}) => {
  // Быстрое изменение веса багажа
  const adjustBaggageWeight = (amount) => {
    const currentWeight = parseFloat(formData.baggageWeight) || 0;
    const newWeight = Math.max(1, currentWeight + amount).toFixed(1);
    
    setFormData(prev => ({
      ...prev,
      baggageWeight: newWeight
    }));
    
    // Очищаем ошибку если она есть
    if (errors.baggageWeight) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.baggageWeight;
        return newErrors;
      });
    }
  };
  
  // Быстрое изменение веса ручной клади
  const adjustCarryOnWeight = (amount) => {
    const currentWeight = parseFloat(formData.carryOnWeight) || 0;
    const newWeight = Math.max(1, currentWeight + amount).toFixed(1);
    
    setFormData(prev => ({
      ...prev,
      carryOnWeight: newWeight
    }));
    
    // Очищаем ошибку если она есть
    if (errors.carryOnWeight) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.carryOnWeight;
        return newErrors;
      });
    }
  };
  
  // Быстрый выбор авиакомпании
  const selectAirline = (airlineId) => {
    setFormData(prev => ({
      ...prev,
      airline: airlineId
    }));
  };
  
  // Быстрый выбор класса обслуживания
  const selectTravelClass = (travelClassId) => {
    setFormData(prev => ({
      ...prev,
      travelClass: travelClassId
    }));
  };
  
  // Быстрый выбор типа направления
  const selectDestinationType = (destinationTypeId) => {
    setFormData(prev => ({
      ...prev,
      destinationType: destinationTypeId
    }));
  };
  
  // Функция расчета
  const calculateLuggage = () => {
    // Здесь должна быть вызвана функция основного расчета
    // В данном случае просто эмулируем действие, так как основная логика 
    // находится в родительском компоненте
    document.querySelector('button[type="submit"]')?.click();
  };
  
  // Получение данных для текущего класса и направления
  const currentAirlineData = airlinesData.find(a => a.id === formData.airline);
  const currentAllowance = currentAirlineData?.baggageAllowance?.[formData.destinationType]?.[formData.travelClass];
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Scale className="w-5 h-5 mr-2 text-purple-500" />
        Быстрая настройка багажа
      </h3>
      
      <div className="space-y-5">
        {/* Вес багажа */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="baggageWeightSlider" className="text-gray-600 dark:text-gray-400">Вес багажа</label>
            <span className="font-medium text-gray-900 dark:text-white">{formData.baggageWeight} кг</span>
          </div>
          <div className="flex items-center space-x-2">
            <button
              type="button"
              onClick={() => adjustBaggageWeight(-1)}
              className="h-7 w-7 flex items-center justify-center rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 hover:bg-purple-200 dark:hover:bg-purple-800"
            >
              <Minus className="w-4 h-4" />
            </button>
            <input
              type="range"
              id="baggageWeightSlider"
              min="1"
              max="60"
              step="1"
              value={formData.baggageWeight}
              onChange={(e) => setFormData(prev => ({ ...prev, baggageWeight: e.target.value }))}
              aria-label="Вес багажа (кг)"
              className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-purple-600"
            />
            <button
              type="button"
              onClick={() => adjustBaggageWeight(1)}
              className="h-7 w-7 flex items-center justify-center rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 hover:bg-purple-200 dark:hover:bg-purple-800"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
          
          {/* Стандартные веса багажа */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {[15, 20, 23, 32].map(weight => (
              <button
                key={weight}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, baggageWeight: weight.toString() }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.baggageWeight == weight 
                    ? 'bg-purple-100 border-purple-300 text-purple-700 dark:bg-purple-900/30 dark:border-purple-700 dark:text-purple-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {weight} кг
              </button>
            ))}
          </div>
        </div>
        
        {/* Вес ручной клади */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="carryOnWeightSlider" className="text-gray-600 dark:text-gray-400">Вес ручной клади</label>
            <span className="font-medium text-gray-900 dark:text-white">{formData.carryOnWeight} кг</span>
          </div>
          <div className="flex items-center space-x-2">
            <button
              type="button"
              onClick={() => adjustCarryOnWeight(-0.5)}
              className="h-7 w-7 flex items-center justify-center rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 hover:bg-purple-200 dark:hover:bg-purple-800"
            >
              <Minus className="w-4 h-4" />
            </button>
            <input
              type="range"
              id="carryOnWeightSlider"
              min="1"
              max="15"
              step="0.5"
              value={formData.carryOnWeight}
              onChange={(e) => setFormData(prev => ({ ...prev, carryOnWeight: e.target.value }))}
              aria-label="Вес ручной клади (кг)"
              className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-purple-600"
            />
            <button
              type="button"
              onClick={() => adjustCarryOnWeight(0.5)}
              className="h-7 w-7 flex items-center justify-center rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 hover:bg-purple-200 dark:hover:bg-purple-800"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
          
          {/* Стандартные веса ручной клади */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {[5, 7, 10, 15].map(weight => (
              <button
                key={weight}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, carryOnWeight: weight.toString() }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.carryOnWeight == weight 
                    ? 'bg-purple-100 border-purple-300 text-purple-700 dark:bg-purple-900/30 dark:border-purple-700 dark:text-purple-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {weight} кг
              </button>
            ))}
          </div>
        </div>
        
        {/* Авиакомпании */}
        <div>
          <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Популярные авиакомпании
          </div>
          <div className="grid grid-cols-2 gap-2">
            {airlinesData.slice(0, 4).map(airline => (
              <button
                key={airline.id}
                type="button"
                onClick={() => selectAirline(airline.id)}
                className={`p-2 text-xs rounded-lg border transition-colors text-center
                  ${formData.airline === airline.id 
                    ? 'bg-purple-100 border-purple-300 text-purple-700 dark:bg-purple-900/30 dark:border-purple-700 dark:text-purple-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {airline.name}
              </button>
            ))}
          </div>
        </div>
        
        {/* Класс обслуживания */}
        <div>
          <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Класс обслуживания
          </div>
          <div className="grid grid-cols-2 gap-2">
            {travelClasses.slice(0, 2).map(travelClass => (
              <button
                key={travelClass.id}
                type="button"
                onClick={() => selectTravelClass(travelClass.id)}
                className={`p-2 text-xs rounded-lg border transition-colors
                  ${formData.travelClass === travelClass.id 
                    ? 'bg-purple-100 border-purple-300 text-purple-700 dark:bg-purple-900/30 dark:border-purple-700 dark:text-purple-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {travelClass.name}
              </button>
            ))}
          </div>
        </div>
        
        {/* Тип направления */}
        <div>
          <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Тип направления
          </div>
          <div className="grid grid-cols-1 gap-2">
            {destinationTypes.map(destinationType => (
              <button
                key={destinationType.id}
                type="button"
                onClick={() => selectDestinationType(destinationType.id)}
                className={`p-2 text-xs rounded-lg border transition-colors
                  ${formData.destinationType === destinationType.id 
                    ? 'bg-purple-100 border-purple-300 text-purple-700 dark:bg-purple-900/30 dark:border-purple-700 dark:text-purple-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {destinationType.name}
              </button>
            ))}
          </div>
        </div>
        
        {/* Информация о текущих нормах */}
        {currentAllowance && (
          <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg border border-blue-100 dark:border-blue-800">
            <h4 className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-1">
              Нормы {currentAirlineData.name}:
            </h4>
            <p className="text-xs text-blue-600 dark:text-blue-400">
              <span className="font-medium">Багаж:</span> {currentAllowance.weight} кг × {currentAllowance.count} мест
            </p>
            <p className="text-xs text-blue-600 dark:text-blue-400">
              <span className="font-medium">Ручная кладь:</span> {currentAllowance.carryOnWeight} кг × {currentAllowance.carryOnCount} мест
            </p>
          </div>
        )}
        
        {/* Кнопка расчета */}
        <Button
          primary
          fullWidth
          onClick={calculateLuggage}
          icon={<Scale className="w-4 h-4" />}
          disabled={isCalculating || Object.keys(errors).length > 0}
          isLoading={isCalculating}
        >
          Рассчитать багаж
        </Button>
      </div>
    </div>
  );
};

export default QuickAdjustmentSidebar;